import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Video, Play, Eye, Calendar, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VideoLessonsPage() {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLesson, setSelectedLesson] = useState(null);

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/video-lessons`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch video lessons');
      }

      const data = await response.json();
      setLessons(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayVideo = async (lesson) => {
    setSelectedLesson(lesson);
    
    // Increment view count
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/video-lessons/${lesson.id}/view`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (err) {
      console.error('Failed to increment view count:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-SG', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-purple-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-5 h-5 text-purple-600" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Video className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Prof.Scaly Video Lessons</h1>
              <p className="text-sm text-slate-600">Watch and learn grammar rules</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center" data-testid="error-message">
            <p className="text-red-700">{error}</p>
          </div>
        ) : lessons.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center" data-testid="no-lessons-message">
            <Video className="w-16 h-16 text-purple-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">No Video Lessons Yet</h2>
            <p className="text-slate-600">Check back soon for exciting grammar video lessons!</p>
          </div>
        ) : (
          <>
            {/* Video Player Modal */}
            {selectedLesson && (
              <div 
                className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                onClick={() => setSelectedLesson(null)}
                data-testid="video-player-modal"
              >
                <div 
                  className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4 flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-white">{selectedLesson.title}</h3>
                      <p className="text-purple-100 text-sm">{selectedLesson.topic}</p>
                    </div>
                    <button
                      onClick={() => setSelectedLesson(null)}
                      className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                      data-testid="close-video-modal-btn"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="p-6">
                    <video
                      src={selectedLesson.video_url?.startsWith('http') ? selectedLesson.video_url : `${API_URL}${selectedLesson.video_url}`}
                      controls
                      autoPlay
                      playsInline
                      className="w-full rounded-xl bg-black"
                      onError={(e) => { e.currentTarget.setAttribute('data-error', '1'); }}
                      data-testid="video-player"
                    >
                      Your browser does not support the video tag.
                    </video>
                    <p className="text-xs text-slate-500 mt-2">
                      If the video does not play, please try a different browser (Chrome, Safari or Edge).
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Video Lessons Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {lessons.map((lesson) => (
                <div
                  key={lesson.id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-purple-100"
                  data-testid={`video-lesson-card-${lesson.id}`}
                >
                  {/* Thumbnail */}
                  <div className="relative bg-gradient-to-br from-purple-400 to-pink-400 aspect-video flex items-center justify-center">
                    {lesson.comic_image_url ? (
                      <img
                        src={lesson.comic_image_url.startsWith('http') ? lesson.comic_image_url : `${API_URL}${lesson.comic_image_url}`}
                        alt={lesson.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Video className="w-16 h-16 text-white/50" />
                    )}
                    <button
                      onClick={() => handlePlayVideo(lesson)}
                      className="absolute inset-0 bg-black/40 hover:bg-black/60 transition-all flex items-center justify-center group"
                      data-testid={`play-video-btn-${lesson.id}`}
                    >
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play className="w-8 h-8 text-purple-600 ml-1" fill="currentColor" />
                      </div>
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-bold text-slate-900 mb-1 line-clamp-2">
                      {lesson.title}
                    </h3>
                    <p className="text-sm text-purple-600 font-semibold mb-3">
                      {lesson.topic}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        <span>{lesson.view_count} views</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(lesson.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
